import React, { FormEvent, useMemo, useState } from 'react';
import { PageTitle, Section } from '../NewDesign/Layout/Layout';
import { Card, CardBody, CardHeader } from '../NewDesign/Card/Card';
import { TextField } from '../NewDesign/Inputs/TextField';
import SelectInput from '../NewDesign/Inputs/SelectInput';
import Radios from '../NewDesign/Inputs/Radios';
import Checkboxes from '../NewDesign/Inputs/Checkboxes';
import divisionsOptions from '../../../Lib/Constants/divisions-options';
import { ButtonClassic } from '../NewDesign/Buttons/Buttons';
import { useGetBuyers, useGetSponsors } from '../../../Lib/Hooks/user.hooks';
import validation from '../../VendorApplicationInit/views/InitiateApplication/InitiateApplication.validation';
import {
  VendorProfileFormData,
  ExistingVendorProfileFormData,
  User,
  VendorProfileFormDataPreSubmit,
} from './types';

const vendorTypeOptions = [
  {
    value: 'Direct',
    label: 'Direct Factory / Manufacturer',
    tooltip: `Refers - A vendor who owns a factory/ factories. Fashion Nova has direct relationship with direct factory/manufacturer in the way business is operated`,
  },
  {
    value: '3rd Party',
    label: 'Importer / Wholesaler',
    tooltip: `Refers - A vendor who is external and has no financial investment in the factory, but works with factories/manufacturers who can produce. Wholesaler will carry their own line/brand and sell to retailers either under their own labels or can custom cut with retailer's label`,
  },
  {
    value: 'Hybrid',
    label: 'Agent',
    tooltip: `Refers - A vendor who represents the direct factory/manufacturer but does not technically own a factory`,
  },
];

const setupForm = (
  currentUser: User,
  creatorName?: string,
  existingFormData?: ExistingVendorProfileFormData,
): VendorProfileFormData => {
  const { first_name: firstName, last_name: lastName } = currentUser;

  const fullName = firstName && lastName ? `${firstName} ${lastName}` : '';

  return {
    creator_name: creatorName || fullName,
    requester_name: fullName,
    requester_id: currentUser.id,
    sponsor_id: existingFormData?.sponsor_id || null,
    existing_vendor: existingFormData?.existing_vendor || 'false',
    legal_name: existingFormData?.legal_name || '',
    doing_business_as: existingFormData?.doing_business_as || null,
    primary_contact_firstname:
      existingFormData?.primary_contact_firstname || '',
    primary_contact_lastname: existingFormData?.primary_contact_lastname || '',
    primary_contact_title: existingFormData?.primary_contact_title || '',
    primary_contact_email: existingFormData?.primary_contact_email || '',
    vendor_type_category: existingFormData?.vendor_type_category || '',
    divisions: existingFormData?.divisions || [],
  };
};

export interface VendorProfileProps {
  creatorName?: string;
  isLoading: boolean;
  handleSubmit: (formData: VendorProfileFormDataPreSubmit) => void;
  submitText: string;
  editMode?: boolean;
  currentUser: User;
  existingFormData?: ExistingVendorProfileFormData;
  onError?: () => void;
  title: string;
}

const vendorExistenceOptions = [
  {
    value: 'false',
    label: 'New Vendor',
  },
  {
    value: 'true',
    label: 'Existing Vendor (Legal Name Change)',
  },
];

function VendorProfile({
  creatorName,
  existingFormData,
  isLoading,
  handleSubmit,
  submitText,
  editMode,
  currentUser,
  onError,
  title,
}: VendorProfileProps) {
  const { data: buyersListData } = useGetBuyers();
  const { data: sponsorListData } = useGetSponsors();
  const [buyerList, setBuyerList] = useState<User[]>([]);
  const [sponsorList, setSponsorList] = useState<User[]>([]);
  const [formData, setFormData] = useState<VendorProfileFormData>(
    setupForm(currentUser, creatorName, existingFormData),
  );
  const [errorField, setErrorField] = useState<string[]>([]);

  useMemo(() => {
    if (buyersListData) {
      setBuyerList(buyersListData.buyers);

      const currentUserNotInBuyerList = buyersListData.buyers.every(
        (buyer: User) => buyer.id !== currentUser.id,
      );
      if (!editMode && currentUserNotInBuyerList) {
        setBuyerList((prevBuyerList) => [
          ...prevBuyerList,
          {
            id: currentUser.id,
            first_name: currentUser.first_name,
            last_name: currentUser.last_name,
          },
        ]);
      }
      if (editMode && existingFormData) {
        if (existingFormData.requester_id !== currentUser.id) {
          const requester = buyersListData?.buyers.find(
            (buyer: User) => buyer.id === existingFormData.requester_id,
          );
          setFormData((prevData: VendorProfileFormData) => ({
            ...prevData,
            requester_id: requester ? requester.id : currentUser.id,
            requester_name: requester
              ? `${requester.first_name} ${requester.last_name}`
              : `${currentUser.first_name} ${currentUser.last_name}`,
          }));
        }
      } else {
        setFormData((prevData: VendorProfileFormData) => ({
          ...prevData,
          requester_id: currentUser.id,
          requester_name: `${currentUser.first_name} ${currentUser.last_name}`,
        }));
      }
    }
    if (sponsorListData) {
      setSponsorList(sponsorListData.sponsors);
    }
  }, [buyersListData, sponsorListData]);

  const handleChange = (field: string, value: string | number | null) => {
    setFormData((prevData: VendorProfileFormData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleRequestorChange = (field: string, value: string | number) => {
    handleChange(field, value);
    if (value) {
      const selectedBuyer = buyerList.find((buyer) => buyer.id === value);
      if (selectedBuyer) {
        handleChange(
          'requester_name',
          `${selectedBuyer.first_name} ${selectedBuyer.last_name}`,
        );
      }
      return;
    }
    handleChange('requester_name', null);
  };

  const handleDbaChange = (field: string, value: string) => {
    let newValue: string | null = value;

    if (value === '' || !value.replace(/\s/g, '').length) newValue = null;
    handleChange(field, newValue);
  };

  const handleMultiSelect = (field: string, value: string) => {
    const isSelected = (
      formData[field as keyof VendorProfileFormData] as Array<string>
    ).includes(value);

    setFormData((prevData: VendorProfileFormData) => ({
      ...prevData,
      [field]: isSelected
        ? (
            prevData[field as keyof VendorProfileFormData] as Array<string>
          ).filter((item: string) => item !== value)
        : [
            ...(prevData[
              field as keyof VendorProfileFormData
            ] as Array<string>),
            value,
          ],
    }));
  };

  const preHandleSubmit = (event: FormEvent) => {
    event.preventDefault();

    const result = validation.validate(formData, {
      abortEarly: false,
    });

    if (result.error) {
      const errors = result.error.details
        .map((detail) => detail.context?.key)
        .filter((key) => key !== undefined) as string[];
      setErrorField(errors);
      if (onError) {
        onError();
      }
      return;
    }
    setErrorField([]);
    handleSubmit({ ...result.value });
  };

  return (
    <form>
      <Section>
        <PageTitle>{title}</PageTitle>
        <Card>
          <CardHeader>Requester Information</CardHeader>
          <CardBody>
            <TextField
              label="Creator Name"
              inputName="creator_name"
              formData={formData}
              onChange={handleChange}
              error={errorField.includes('creator_name')}
              disabled
            />
            <SelectInput
              inputName="requester_id"
              label="Requestor name"
              formData={formData}
              options={buyerList.map((buyer) => ({
                value: buyer.id,
                label: `${buyer.first_name} ${buyer.last_name}`,
              }))}
              onChange={handleRequestorChange}
              error={errorField.includes('requester_id')}
              required
              disabled={editMode}
              testId="requester_name-input"
            />
            <SelectInput
              inputName="sponsor_id"
              label="Sponsor name"
              formData={formData}
              options={sponsorList.map((sponsor) => ({
                value: sponsor.id,
                label: `${sponsor.first_name} ${sponsor.last_name}`,
              }))}
              onChange={handleChange}
              error={errorField.includes('sponsor_id')}
              required
              testId="sponsor_name-input"
            />
          </CardBody>
        </Card>
      </Section>

      <Section>
        <Card>
          <CardHeader>Supplier / Vendor Contact Information</CardHeader>
          <CardBody>
            <Radios
              options={vendorExistenceOptions}
              formData={formData}
              label="Is this a new or existing vendor?"
              inputName="existing_vendor"
              error={errorField.includes('existing_vendor')}
              required
              onChange={(name: string, value: string) => {
                handleChange(name, value);
              }}
            />

            <TextField
              inputName="legal_name"
              label="Entity Legal Name"
              formData={formData}
              onChange={handleChange}
              error={errorField.includes('legal_name')}
              required
            />
            <TextField
              inputName="doing_business_as"
              label="Doing Business As (DBA), if any"
              formData={formData}
              onChange={handleDbaChange}
              error={errorField.includes('doing_business_as')}
            />

            <Radios
              options={vendorTypeOptions}
              formData={formData}
              label="What type of vendor is this?"
              error={errorField.includes('vendor_type_category')}
              inputName="vendor_type_category"
              required
              onChange={handleChange}
              disabled={editMode}
            />

            <Checkboxes
              options={divisionsOptions}
              formData={formData}
              error={errorField.includes('divisions')}
              label="What divisions can this vendor supply?"
              inputName="divisions"
              onChange={handleMultiSelect}
              required
            />
          </CardBody>
        </Card>
      </Section>

      <Section>
        <Card>
          <CardHeader>Vendor point of contact</CardHeader>
          <CardBody>
            <TextField
              inputName="primary_contact_firstname"
              label="First Name"
              formData={formData}
              onChange={handleChange}
              error={errorField.includes('primary_contact_firstname')}
              required
            />
            <TextField
              inputName="primary_contact_lastname"
              label="Last Name"
              formData={formData}
              onChange={handleChange}
              error={errorField.includes('primary_contact_lastname')}
              required
            />
            <TextField
              inputName="primary_contact_title"
              label="Title"
              formData={formData}
              onChange={handleChange}
              error={errorField.includes('primary_contact_title')}
            />
            <TextField
              inputName="primary_contact_email"
              label="Email Address"
              formData={formData}
              onChange={handleChange}
              error={errorField.includes('primary_contact_email')}
              required
              disabled={editMode}
            />
          </CardBody>
        </Card>
        <ButtonClassic onClick={preHandleSubmit} loading={isLoading}>
          {submitText}
        </ButtonClassic>
      </Section>
    </form>
  );
}

export default VendorProfile;
